// envSwitch.ts — CORE-413: runtime dev/prod environment selector for non-prod
// builds. A dev or preview build bakes ONE env's hosts at build time
// (EXPO_PUBLIC_API_URL etc.). This lets a tester flip the whole app between the
// dev and prod backends at runtime — no rebuild — by rewriting the api/chat
// hosts at the fetch + SignalR-hub layers.
//
// Gated hard: only builds carrying EXPO_PUBLIC_ENV_SWITCHER=true (dev / preview
// / development-simulator EAS profiles) — or a dev client (__DEV__) — honour an
// override. Production store builds omit the flag, so an override is inert and
// the switcher UI never shows.
//
// Composes with the RU-first resolver (apiEndpoint.ts): env-switch picks the
// base env first, then RU-first applies within prod.

import { Platform } from 'react-native';

import { storage } from '../utils/storage';
import { DEV_RU_EDGE_HOSTS, isRuDevEdgeWebHost } from './ruDevEdge';
import { isRuStagingEdgeWebHost, toStagingHost } from './stagingEnv';

export type EnvName = 'dev' | 'prod';

/**
 * CORE-1507 — what the switcher can be set to. Wider than `EnvName` on purpose:
 * `EnvName` stays the web's two-way «prod or not» answer (CORE-1103), and the
 * stand is a third backend that only a native test build is pointed at.
 */
export type EnvChoice = EnvName | 'staging';

// Host sets per env. Rewriting maps a host from the OTHER env to the selected
// env's counterpart for the same role, so a build baked with one env's hosts can
// be pointed at the other. Keep in sync with .env.production / .env.development.
const HOSTS: Record<EnvName, Record<string, string>> = {
  dev: {
    api: 'api.agdev.tmless.org',
    chatApi: 'chat-api.agdev.tmless.org',
    chatHub: 'agape-chat.agdev.tmless.org',
  },
  prod: {
    api: 'api.agapella.io',
    chatApi: 'chat-api.agapella.io',
    chatHub: 'agape-chat.agapella.io',
  },
};

type Role = keyof (typeof HOSTS)['dev'];

/**
 * CORE-1422 — where a request for the DEV backend actually goes.
 *
 * Native: through the Moscow edge by default (owner decision 15.09.2026). A
 * tester in Russia on a test build switched to dev could not reach anything —
 * the build went straight to *.agdev.tmless.org, a leg that is shaped from
 * Russia — while the same account worked on web (Vercel's /api) and on prod
 * (RU-first). `EXPO_PUBLIC_DEV_BACKEND_DIRECT=true` keeps a build on the direct
 * hosts — the `e2e` EAS profile sets it: its emulators run in Germany, where the
 * Moscow hop only adds latency and a long-lived socket cut.
 *
 * Web: the direct dev hosts, except on a page served from the edge itself
 * (`dev.agdev.tmless.org`, `*.rudev.tmless.org`).
 */
function devBackendViaRuEdge(): boolean {
  if (Platform.OS !== 'web') return process.env.EXPO_PUBLIC_DEV_BACKEND_DIRECT !== 'true';
  if (typeof window === 'undefined' || !window.location) return false;
  return isRuDevEdgeWebHost(window.location.hostname || '');
}

/** CORE-1589 — the stand's page served from its Moscow edge (app.ru.ag1). */
function stagingWebViaRuEdge(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof window === 'undefined' || !window.location) return false;
  return isRuStagingEdgeWebHost(window.location.hostname || '');
}

/** Host (with the edge path prefix, when there is one) for `role` in `env`. */
function targetHost(env: EnvName, role: Role): string {
  const edge = (DEV_RU_EDGE_HOSTS as Record<string, string>)[role];
  return env === 'dev' && edge && devBackendViaRuEdge() ? edge : HOSTS[env][role];
}

/**
 * The only hostnames that are production. Anything else the web app is served
 * from — rn-agape.vercel.app, a preview URL, localhost — is by definition not
 * production.
 */
const PROD_WEB_HOSTS = ['agapella.io', 'app.agapella.io', 'app-ru.agapella.io'];

/**
 * The production-like stand (CORE-634). It is not production and it is not dev:
 * it runs its own backend at api.ag1.tmless.org, off the `staging` branch.
 */
const STAGING_WEB_HOSTS = ['app.ag1.tmless.org', 'app.ru.ag1.tmless.org'];

/**
 * Which surface the web app is served from — a strictly different question from
 * `runtimeWebEnv` below, which answers «against which backend env».
 *
 * CORE-1103: the two were one answer, and `EnvName` has no room for a third
 * value, so the stand's pill read «env: dev» while the app talked to the
 * stand's own API. QA reads that pill to say where a bug was seen; a pill that
 * names the wrong stand is the same class of defect CORE-631 fixed for the
 * build id.
 */
export type WebSurface = 'prod' | 'staging' | 'dev';

export function runtimeWebSurface(): WebSurface | null {
  if (Platform.OS !== 'web') return null;
  if (typeof window === 'undefined' || !window.location) return null;
  const host = window.location.hostname;
  if (!host) return null;
  if (PROD_WEB_HOSTS.includes(host)) return 'prod';
  if (STAGING_WEB_HOSTS.includes(host)) return 'staging';
  return 'dev';
}

/**
 * Which env the app is *running* in, decided from the address bar rather than
 * from a build-time variable.
 *
 * Web builds on Vercel are compiled with NODE_ENV=production even for the dev
 * preview, so `.env.production` is loaded there too and its prod hosts get
 * baked into the dev bundle. That is how the dev stand ended up talking to the
 * prod SignalR hub twice (CORE-407, and again after the Vercel var was lost) —
 * the prod hub's CORS rejects the dev origin, so chat simply died. A Vercel
 * env var fixed it both times and both times something un-fixed it.
 *
 * The host can't be lost or mistyped: if we're not on a prod domain, we're not
 * in prod. Native has no address bar, so there this returns null and the
 * build-time variable stays authoritative — which is correct on native, where
 * each EAS profile really does bake its own env.
 */
export function runtimeWebEnv(): EnvName | null {
  // Expressed through the surface so there is ONE host list. The stand is a
  // non-prod surface, therefore a non-prod backend env — which is what
  // `hostForRole` and CommunityFeed (CORE-940) need, and what they got before
  // CORE-1103 split the display label off. Behaviour is unchanged by
  // construction, not by inspection.
  const surface = runtimeWebSurface();
  return surface === null ? null : surface === 'prod' ? 'prod' : 'dev';
}

/**
 * Host for a given role (api / chatApi / chatHub), resolved for the env we are
 * actually running in. Falls back to the caller's build-time value when the
 * runtime env is unknown (native) or the role isn't one we map.
 */
export function hostForRole(role: Role, buildTimeUrl: string): string {
  // CORE-1589 — the stand's bundle bakes the stand's direct hosts; opened from
  // the Moscow edge it must not go back onto the shaped leg.
  if (stagingWebViaRuEdge()) return toStagingHost(buildTimeUrl, [], true);
  const env = runtimeWebEnv();
  if (!env) return buildTimeUrl;
  const want = targetHost(env, role);
  if (!want) return buildTimeUrl;
  // Only rewrite when the baked URL points at the *other* env's host for the
  // same role; anything custom is left alone.
  const others = (Object.keys(HOSTS) as EnvName[]).map((e) => HOSTS[e][role]).filter(Boolean);
  for (const from of others) {
    if (buildTimeUrl.includes(`://${from}`)) return buildTimeUrl.replace(`://${from}`, `://${want}`);
  }
  return buildTimeUrl;
}

const STORAGE_KEY = 'agape_env_override';

let override: EnvChoice | null = null; // loaded once at startup, read live by the rewrite

/** Whether the switcher is allowed in this build. */
export function isEnvSwitchEnabled(): boolean {
  return process.env.EXPO_PUBLIC_ENV_SWITCHER === 'true' || (typeof __DEV__ !== 'undefined' && __DEV__);
}

/**
 * CORE-631 — whether to show the build pill (env + version, tap to copy).
 *
 * Wider than `isEnvSwitchEnabled`: the pill is *read-only* information, and its
 * audience is QA, who test on the web dev stand, not on EAS builds. The stand
 * carries no `EXPO_PUBLIC_ENV_SWITCHER`, so the pill was invisible exactly where
 * bug reports are written. Decided from the address bar for the same reason
 * `runtimeWebEnv` is: anything that isn't a prod host is a test surface, and no
 * Vercel variable can be lost or mistyped into showing it to end users.
 *
 * Switching env stays gated on `isEnvSwitchEnabled` — on the web stand the REST
 * base is Vercel's `/api` rewrite, which an override cannot redirect, so a
 * switch there would half-apply and mislead.
 */
export function isBuildChipVisible(): boolean {
  return isEnvSwitchEnabled() || runtimeWebEnv() === 'dev';
}

/**
 * CORE-1556 — width a row pinned to the bottom-right corner must leave free on
 * its right so the floating build chip (EnvSwitcher: 10 px from the edge, ~95 px
 * wide on the stand) does not sit on top of its controls. 0 where the chip is
 * not rendered, so the production layout is untouched.
 */
export function buildChipInsetRight(): number {
  return isBuildChipVisible() ? 124 : 0;
}

/** The env this build was compiled against (from the baked API host). */
export function buildEnv(): EnvName {
  return (process.env.EXPO_PUBLIC_API_URL || '').includes('agapella.io') ? 'prod' : 'dev';
}

/** Current override, if any (null = use the build default). */
export function getEnvOverride(): EnvChoice | null {
  return override;
}

/** The env actually in effect right now. */
export function activeEnv(): EnvChoice {
  return override ?? buildEnv();
}

/** Load the persisted override at startup. No-op (and ignored) when disabled. */
export async function loadEnvOverride(): Promise<void> {
  if (!isEnvSwitchEnabled()) {
    override = null;
    return;
  }
  try {
    const v = await storage.getJSON<EnvChoice>(STORAGE_KEY);
    override = v === 'dev' || v === 'prod' || v === 'staging' ? v : null;
  } catch {
    override = null;
  }
}

/** Persist (or clear) the override. Takes effect on the next app reload. */
export async function setEnvOverride(env: EnvChoice | null): Promise<void> {
  override = env;
  try {
    await storage.setJSON(STORAGE_KEY, env);
  } catch {
    // best-effort; the in-memory value still holds for this session
  }
}

/**
 * Rewrite a URL's api/chat host to the env in effect. Only touches known hosts
 * (api / chat-api / chat-hub); everything else (S3, api-ru, third-party) passes
 * through. Anchored to `://<host>` so query strings can't be corrupted.
 *
 * With an override, the other env's hosts map to the selected env. CORE-1422 —
 * when the env in effect is dev and dev goes through the Moscow edge (see
 * `devBackendViaRuEdge`), the direct dev hosts map onto the edge as well. That
 * holds without an override too — for a native build baked with dev hosts and
 * for a page served from the dev edge. A prod build with no override, and every
 * other web page, is left alone.
 *
 * CORE-1507 — the stand is a third target with a rewrite of its own
 * (stagingEnv.ts). On native it goes through its Moscow edge under the same
 * rule as dev, `EXPO_PUBLIC_DEV_BACKEND_DIRECT` included. CORE-1589 — and on
 * web, the stand's own page opened from that edge (app.ru.ag1) stays on it.
 */
export function applyEnvHost(url: string): string {
  if (!url) return url;
  const selected = override && isEnvSwitchEnabled() ? override : null;
  if (selected === 'staging') {
    return toStagingHost(url, Object.values(HOSTS), Platform.OS !== 'web' && devBackendViaRuEdge());
  }
  if (selected === null && stagingWebViaRuEdge()) return toStagingHost(url, [], true);
  const switching = selected !== null;
  // Without an override: a native build baked with dev hosts, or a page served
  // from the dev edge (a stray absolute dev URL there must not go direct either).
  const unswitchedDev = Platform.OS === 'web' ? devBackendViaRuEdge() : buildEnv() === 'dev';
  const env: EnvName | null = switching ? selected : (unswitchedDev ? 'dev' : null);
  if (!env) return url;
  const other: EnvName = env === 'dev' ? 'prod' : 'dev';
  for (const role of Object.keys(HOSTS[other]) as Role[]) {
    const to = targetHost(env, role);
    const froms = switching ? [HOSTS[other][role]] : [];
    if (to !== HOSTS[env][role]) froms.push(HOSTS[env][role]);
    for (const from of froms) {
      if (to && url.includes(`://${from}`)) return url.replace(`://${from}`, `://${to}`);
    }
  }
  return url;
}
